/* 产品参数对比：横向滚动的辅助控件。

   桌面端与移动端现在共用同一个滚动容器（整张表横向滚动），区别只是列宽/是否需要按钮：
   - 桌面端：列多到放不下时才可滚动，这时才出现左右按钮。
   - 移动端：列宽本来就调窄到"2 又 1/5 列"，永远可滚动，按钮隐藏，改由底部滚动指示条 + 原生触摸滑动。

   平滑移动用 scrollBy({ behavior: 'smooth' })，是浏览器原生带缓动的滚动，不是生硬跳变。 */
class ProductComparisonNav extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-comparison-viewport]');
    this.prevButton = this.querySelector('[data-comparison-prev]');
    this.nextButton = this.querySelector('[data-comparison-next]');
    this.scrollbarThumb = this.querySelector('[data-comparison-scrollbar-thumb]');

    if (!this.viewport) return;

    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));

    this.onScroll = () => this.scheduleUpdate();
    this.viewport.addEventListener('scroll', this.onScroll);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.viewport);
    } else {
      this.onResize = () => this.scheduleUpdate();
      window.addEventListener('resize', this.onResize);
    }

    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.viewport?.removeEventListener('scroll', this.onScroll);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    clearTimeout(this.updateTimer);
  }

  /* 一次移动一列，落点始终对齐列边界 */
  get stepSize() {
    const cell = this.querySelector('[data-comparison-cell]');
    return cell ? cell.offsetWidth : Math.round(this.viewport.clientWidth * 0.6);
  }

  step(direction) {
    this.viewport.scrollBy({ left: direction * this.stepSize, behavior: 'smooth' });
  }

  scheduleUpdate() {
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => this.update(), 80);
  }

  update() {
    const { viewport } = this;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    const scrollable = maxScroll > 1;

    this.classList.toggle('is-scrollable', scrollable);

    const current = viewport.scrollLeft;
    if (this.prevButton) this.prevButton.disabled = current <= 1;
    if (this.nextButton) this.nextButton.disabled = current >= maxScroll - 1;

    this.updateScrollbar(viewport, maxScroll, current);
    this.positionScrollbarTrack();
  }

  /* 走查要求把滚动指示条从表格最下方挪到"产品名称胶囊下方、第一条参数行上方"。
     指示条已经在 CSS 里改成 position: absolute（脱离文档流，避免拆表格搞两套同步滚动），
     这里按当前实际渲染出来的产品名胶囊底部位置算出 top，而不是写死一个数值——
     图片尺寸、产品名会不会换行都会影响表头实际高度，量出来的才准。
     没有产品名胶囊（都没填名称）时依次退到产品图、再退到整个表头行做参照。 */
  positionScrollbarTrack() {
    const track = this.scrollbarThumb?.parentElement;
    if (!track) return;

    const anchor =
      this.querySelector('.product-comparison__name') ||
      this.querySelector('.product-comparison__media') ||
      this.querySelector('.product-comparison__row--header');
    if (!anchor) return;

    const anchorBottom = anchor.getBoundingClientRect().bottom;
    const wrapTop = this.getBoundingClientRect().top;
    // +40 对应 CSS 里表头单元格原有的 40px（4rem）留白，让指示条紧贴在那条原有间距之后，
    // 不是凭空再加一段新间距。
    track.style.top = `${anchorBottom - wrapTop + 40}px`;
  }

  /* 滚动指示条：滑块宽度 = 可视区域占总内容的比例，位置 = 当前滚动进度 */
  updateScrollbar(viewport, maxScroll, current) {
    if (!this.scrollbarThumb) return;

    if (!scrollableCheck(maxScroll)) {
      this.scrollbarThumb.style.width = '100%';
      this.scrollbarThumb.style.left = '0';
      return;
    }

    const visibleRatio = Math.min(1, viewport.clientWidth / viewport.scrollWidth);
    const progress = current / maxScroll;

    this.scrollbarThumb.style.width = `${visibleRatio * 100}%`;
    this.scrollbarThumb.style.left = `${progress * (1 - visibleRatio) * 100}%`;
  }
}

function scrollableCheck(maxScroll) {
  return maxScroll > 1;
}

if (!customElements.get('product-comparison-nav')) {
  customElements.define('product-comparison-nav', ProductComparisonNav);
}
